import React, {useState} from "react";
import {useNavigate} from "react-router";

import iconInfo from "url:shared-assets/icons/white/information-circle.svg";
import iconDown from "url:shared-assets/icons/chevronDown.svg";
import iconStarOrange from "url:shared-assets/icons/orange/star.svg";
import iconStarGreen from "url:shared-assets/icons/green/star.svg";
import iconPlus2 from "url:shared-assets/icons/white/plus.svg";
import iconMinus from "url:shared-assets/icons/minus.svg";
import CustomButton from "../../components/CustomButton";
import HealthBadge from "../../components/Badges/HealthBadge";
import SurveyBadge from "../../components/Badges/SurveyBadge";
import TwoBars from "../../components/Bars/TwoBars";
import HalfCircleChart from "../../components/HalfCircleChart";
import ResultInfoModal from "./ResultInfoModal";
import {
  AiInsights,
  FeedbackType,
  LinkRequestInterface,
  SurveyQuestionInterface,
  SurveyResultInterface
} from "./interfaces";
import {showLanguageString} from "../../lang/helpers";
import {translateQuestion, translateSection} from "./translations_name";
import Api from "../../services/Api";
import {getGoodScore} from "./helpers";

interface Props {
  allQuestions: Array<SurveyQuestionInterface>;
  surveyResult: SurveyResultInterface;
  aiInsights: AiInsights;
  lang: string;
  feedbackType: FeedbackType;
  t: Function;
  setFocusAreasSorted: Function;
  otherRequests: Array<LinkRequestInterface>
  canCreateRequest: boolean
  surveyId: string;
  clientName: string;
}

enum ArrayName {
  QUESTION,
  GOOD,
  BAD
}

const SurveyResultSummary = ({allQuestions, surveyResult, aiInsights, lang,
                               setFocusAreasSorted, surveyId, otherRequests, canCreateRequest, feedbackType, t,
                               clientName} : Props) => {
  const api = Api.getInstance();
  const navigate = useNavigate();
  const [showValidatedStrength, setShowValidatedStrength] = useState(false);
  const [showHiddenStrength, setShowHiddenStrength] = useState(false);
  const [showBlindSpot, setShowBlindSpot] = useState(false);
  const [showGrowth, setShowGrowth] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const hasTopHealth = surveyResult.focusAreas.filter(f => f.healthLevel >= 2).length;
  const hasImprovementHealth = surveyResult.focusAreas.filter(f => f.healthLevel < 2).length;
  // +Show more
  const SHOW_MORE_INCREMENT = 3;
  const [visibleCount, setVisibleCount] = useState(SHOW_MORE_INCREMENT);
  const [showMoreBadCount, setShowMoreBadCount] = useState(SHOW_MORE_INCREMENT);
  const [showMoreGoodCount,setShowMoreGoodCount] = useState(SHOW_MORE_INCREMENT)
  const setterShowMoreMap = {
    [ArrayName.QUESTION]: setVisibleCount,
    [ArrayName.GOOD]: setShowMoreGoodCount,
    [ArrayName.BAD]: setShowMoreBadCount
  }

  const splitLongText = (txt: string, part: number) => {
    const chars = txt.length;
    let pos =  chars / 2;
    while (pos < chars) {
      if (txt.charAt(pos) === ".") {
        if (part === 0) {
          return txt.substring(0, pos + 1);
        } else {
          return txt.substring(pos + 1);
        }
      }
      pos++;
    }
    // Catcher in the rye - found no '.'
    if (part === 0) {
      return txt;
    }
  }

  const normalizeFromZeroToOne = (value: number, min: number, max: number) => {
    // From 0-1 to min-max
    return value * (max - min) + min;
  }

  const handleShowMore =  (islimt:boolean, arrayName:ArrayName) => {
    console.log('Change show more counter');
    setterShowMoreMap[arrayName](islimt ? 3 : (prev) => prev +3 )
   }

  const renderQuestion = (question: SurveyQuestionInterface, badgeText: string, cls: string) => {
    return (
      <div className="mt-2 bg-indigo-800 rounded-md p-4 border-indigo-700 border" key={"sq_" + question.id}>

        <div className="flex">
          <div className={cls}>{badgeText}</div>
          <div className="ml-2 z-badge transparent truncate sm:truncate-none">{translateSection(lang, question.focusArea)}</div>
        </div>

        <div className="flex flex-col md:flex-row mt-2 gap-4">
        <div className="flex-1 text-gray-300">
            {translateQuestion(lang, question, clientName)}
          </div>
          <div className="flex-1">
            <TwoBars value1={question.scoreSelf} value2={question.scoreOther} max={surveyResult.nrAnswerOptions}
                     size="small"/>
            {/* Hidde in mobile view  */}
            <div className="mt-2 hidden md:flex">
              <div className="text-sm">
                {t("self-assessment")}: <span className="z-title2">{question.scoreSelf.toFixed(1)}</span>/{surveyResult.nrAnswerOptions}
              </div>
              <div className="ml-4 text-sm">
                Feedback: <span className="z-title2">{feedbackType === FeedbackType.ONLY_SELF ? "-" : question.scoreOther.toFixed(1)}</span>/{surveyResult.nrAnswerOptions}
              </div>
              <div className="ml-4 text-sm">
                {t("perception-gap")}: <span className="z-title2">{feedbackType === FeedbackType.ONLY_SELF ? "-" : Math.abs(question.scoreOther - question.scoreSelf).toFixed(1)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderQuestions = (type: string) => {
    let _questions: Array<SurveyQuestionInterface>
    let badgeText = "Unknown";
    let colorVal: number | undefined;

    if (type === 'validated_strengths') {
      colorVal = 4;
      if (feedbackType === FeedbackType.ONLY_SELF) {
        badgeText = lang === "sv" ? "Styrka": "Strength";
        _questions = allQuestions.filter((q) => (q.scoreSelf >= 4))
          .sort((a, b) => {return a.scoreSelf < b.scoreSelf ? 1 : -1});
      } else {
        badgeText = lang === "sv" ? "Validerad styrka": "Validated strength";
        _questions = allQuestions.filter((q) => (q.scoreSelf >= 4 && q.scoreOther >= 4))
          .sort((a, b) => {return a.scoreOther < b.scoreOther ? 1 : -1});
      }

    } else if (type === 'hidden_strengths') {
      badgeText = lang === "sv" ? "Dold styrka": "Hidden strength";
      colorVal = 3;
      _questions = allQuestions.filter((q) => (q.scoreSelf < 4 && q.scoreOther >= 4))
        .sort((a, b) => {return a.scoreOther < b.scoreOther ? 1 : -1});
    } else if (type === 'blind_spots') {
      badgeText = lang === "sv" ? "Blinda fält": "Blind Spot";
      colorVal = 2;
      _questions = allQuestions.filter((q) => (q.scoreSelf >= 4 && q.scoreOther < 4))
        .sort((a, b) => {return a.scoreOther > b.scoreOther ? 1 : -1});
    } else if (type === 'growth_priorities') {
      badgeText = lang === "sv" ? "Utvecklingsfokus": "Growth priority";
      colorVal = 1;
      _questions = allQuestions.filter((q) => (q.scoreSelf < 4 && q.scoreOther < 4))
        .sort((a, b) => {return a.scoreOther > b.scoreOther ? 1 : -1});
    } else {
      _questions = [];
    }
    
    const cls = colorVal ? `z-badge truncate sm:truncate-none zb-${colorVal}` : "z-badge truncate sm:truncate-none";
    const isShowingAll =  visibleCount >= _questions.length
  
    if (_questions.length === 0) {
      return (
        <div className="mt-4">There are no {badgeText}</div>
      )
    }
    return (
      <>
        {_questions.slice(0, visibleCount).map(question => {
              return renderQuestion(question, badgeText, cls);
        })}

        {_questions.length > 3 && (
          <CustomButton 
              buttonType="transparentBase" 
              className="mt-4 mx-auto cursor-pointer" 
              size="sm" 
              onClick={() =>handleShowMore(isShowingAll, ArrayName.QUESTION)}
              leftIcon={<img src={isShowingAll ? iconMinus: iconPlus2} alt="info" />} 
              style={{ width: "unset" }} 
            > 
            {isShowingAll ? t("btn-show-less") : t("btn-show-more")}
          </CustomButton> 
        )}
      </>
    )
  }

  const showTopHealthFocusAreas = (hasAreas: number) => {
    let validatedStrengths = 0;
    let hiddenStrengths = 0;
    const _goodScore = getGoodScore(surveyResult.nrAnswerOptions);
    const goodArea =surveyResult.focusAreas.sort((a, b) => {if (a.healthLevel !== b.healthLevel) return a.healthLevel < b.healthLevel ? 1 : -1;
                return a.health < b.health ? 1 : -1;}).filter(f => f.healthLevel >= 2)
    const isShowMoreGood =  showMoreGoodCount >= goodArea.length;
    for (let focusArea of surveyResult.focusAreas) {
      for (let q of focusArea.questions) {
        if (feedbackType === FeedbackType.ONLY_SELF) {
          if (q.scoreSelf >= _goodScore) {
            validatedStrengths += 1;
          }
        } else if (q.scoreOther >= _goodScore) {
          if (q.scoreSelf >= _goodScore) {
            validatedStrengths += 1;
          } else {
            hiddenStrengths += 1;
          }
        }
      }
    }

    if (validatedStrengths === 0 && hiddenStrengths === 0 && hasAreas === 0) {
      return (
        <div className="text-center">
        <img src={iconStarOrange} alt="star" className="mt-4 h-6 w-6 mx-auto"/>
        <div className="mx-auto mt-4 mb-4 w-full max-w-sm px-4 text-center text-gray-400">
            {
              lang === "sv" 
                ? "Inga styrkor identifierades i den här feedbackomgången, vilket pekar på ett behov av att reflektera och aktivt arbeta med din utveckling." 
                : "No strengths were identified in this feedback round, indicating a need to reflect and actively work on your development."
            }
          </div>
        </div>
      );
    }

    return (
      <div>
        {
          feedbackType === FeedbackType.FEEDBACK &&
            <>
              <div className="mt-8 font-medium text-gray-100">
                {lang === "sv" ? "Insikter" : "Insights"}
              </div>
              <div className="text-gray-400 mt-1 whitespace-pre-line">
                {aiInsights.strengths}
              </div>
            </>
        }

        <p className="font-medium mt-4">{lang === "sv" ? "Främsta områden" : "Top Areas"}</p>
        <div className="mt-2 text-gray-400">
          {
            hasAreas === 0 ?
              lang === "sv" ? "Inget område har nått till nivån god eller utmärkt." : "No Areas reached strong overall levels." :
              lang === "sv" ? "Visar områden med god eller utmärkt status" :
                "Showing Areas with Excellent or Good Health levels."
          }
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {
            goodArea.slice(0,showMoreGoodCount)
              .map((focusArea, i) => {
                const aiFocusArea = findAiFocusArea(focusArea.id);
                return (
                  <div className="bg-indigo-800 rounded-[6px] p-4 main-third-col border-indigo-700 border-1" key={"top_health_" + i}>
                    <div className="flex justify-between md:flex-row md:justify-between">
                      <p>{translateSection(lang, focusArea)}</p>
                      <HealthBadge value={focusArea.health} level={focusArea.healthLevel} lang={lang}/>
                    </div>
                    <div className="mt-3">
                      <TwoBars value1={focusArea.scoreSelf} value2={focusArea.scoreOther}
                               max={surveyResult.nrAnswerOptions}
                               results1={focusArea.scoreSelf.toFixed(1) + "/" + surveyResult.nrAnswerOptions}
                               results2={focusArea.scoreOther.toFixed(1) + "/" + surveyResult.nrAnswerOptions}/>
                    </div>
                    {
                      aiFocusArea &&
                        <div className="mt-3 text-sm text-gray-400">{aiFocusArea.summary}</div>
                    }
                  </div>
                )
              })
          }
        </div>
{/* Strengths-Top Areas
NOTE: Need to add show more logic
 */}
        {
          surveyResult.focusAreas.filter(f => f.healthLevel >= 2).length > 3 &&
          <CustomButton 
              buttonType="transparentBase" 
              className="mt-4 mx-auto cursor-pointer" 
              size="sm" 
              onClick={() => handleShowMore(isShowMoreGood, ArrayName.GOOD)}
              leftIcon={<img src={isShowMoreGood ? iconMinus: iconPlus2} alt="info" />} 
              style={{ width: "unset" }} 
              > 
              {isShowMoreGood ? t("btn-show-less") : t("btn-show-more")}
          </CustomButton> 
         }

        <hr className="border-indigo-700 my-4"/>

        {
          validatedStrengths > 0 &&
            <>
              <div className="flex justify-between items-center cursor-pointer"
                   onClick={() => {
                    setVisibleCount(SHOW_MORE_INCREMENT)
                    setShowValidatedStrength(!showValidatedStrength)}}>
                  <p>{t("validated-strengths")} ({validatedStrengths})</p>
                  <img src={iconDown} alt="open"
                       className={"h-4 w-4 hover:bg-indigo-700 rounded-[12px]" + (showValidatedStrength ? " rotate-180" : "")}/>
              </div>

              {showValidatedStrength && renderQuestions('validated_strengths')}

              <hr className="border-indigo-700 my-4"/>
            </>
        }


        {
          hiddenStrengths > 0 &&
            <>
              <div className="flex justify-between items-center cursor-pointer"
                 onClick={() => {
                  setVisibleCount(SHOW_MORE_INCREMENT)
                  setShowHiddenStrength(!showHiddenStrength)}}>
                <p>{t("hidden-strengths")} ({hiddenStrengths})</p>
                <img src={iconDown} alt="open"
                     className={"h-4 w-4 hover:bg-indigo-700 rounded-[12px]" + (showHiddenStrength ? " rotate-180" : "")}/>
              </div>

              {showHiddenStrength && renderQuestions('hidden_strengths')}

              <hr className="border-indigo-700 mt-4"/>
            </>
        }

      </div>
    )
  }

  const findAiFocusArea = (focusAreaId: string) => {
    if (!aiInsights.focusAreas) {
      return;
    }
    for (const fa of aiInsights.focusAreas) {
      if (fa.id === focusAreaId) {
        return fa;
      }
    }
  }

  const showTopImprovementFocusAreas = (hasAreas: number) => {
    let blindSpots = 0;
    let growthPriorities = 0;
    const _goodScore = getGoodScore(surveyResult.nrAnswerOptions);
    const badArea = surveyResult.focusAreas.sort((a, b) => {if (a.healthLevel !== b.healthLevel) return a.healthLevel < b.healthLevel ? -1 : 1;
                return a.health < b.health ? -1 : 1; }).filter(f => f.healthLevel < 2)
    const isShowMoreBad =  showMoreBadCount >= badArea.length;
    for (let focusArea of surveyResult.focusAreas) {
      for (let q of focusArea.questions) {
        if (feedbackType === FeedbackType.ONLY_SELF) {
          if (q.scoreSelf < _goodScore) {
            growthPriorities += 1;
          }
        } else  if (q.scoreOther < _goodScore) {
          if (q.scoreSelf < _goodScore) {
            growthPriorities += 1;
          } else {
            blindSpots += 1;
          }
        }
      }
    }
    if (hasAreas === 0 && blindSpots === 0 && growthPriorities === 0) {
      return (
        <div className="text-center">
          <img src={iconStarGreen} alt="star" className="mt-4 h-6 w-6 mx-auto"/>
          <div className="mx-auto mt-4 w-90 text-center text-gray-400">
            {
              lang === "sv" ? "Starkt jobbat! Inga svagheter identifierades i den här feedbacken. Håll i det som fungerar och fortsätt vässa dina styrkor." :
                "Great work! No weaknesses were identified in this round of feedback! Keep doing what works and continue sharpening your strengths."
            }
          </div>
        </div>
      )
    }
    return (
      <div>
        {
          feedbackType === FeedbackType.FEEDBACK &&
            <>
              <div className="mt-8 font-medium text-gray-100">
                {lang === "sv" ? "Insikter" : "Insights"}
              </div>

              <div className="text-gray-400 mt-1 whitespace-pre-line">
                {aiInsights.improvements}
              </div>
            </>
        }

        <p className="font-medium mt-4">
          {lang === "sv" ? "Områden med utvecklingspotential" : "Areas with improvement potential"}
        </p>
        <div className="mt-2 text-gray-400">
          {
            hasAreas === 0 ?
              lang === "sv" ? "Inga svaga områden identifierade." : "No weak areas identified." :
              lang === "sv" ? "Visar områden med svag eller måttlig status" :
                "Showing Areas with Poor or Moderate Health levels."
          }

        </div>
        
        <div className="mt-4 flex flex-wrap gap-2">
          {
            badArea.slice(0, showMoreBadCount)
              .map((focusArea, i) => {
                const aiFocusArea = findAiFocusArea(focusArea.id);
                return (
                  <div className="bg-indigo-800 rounded-[6px] p-4 main-third-col border-indigo-700 border-1" key={"top_health_" + i}>
                    <div className="flex flex-wrap items-start">
                      <div className="flex-1">{translateSection(lang, focusArea)}</div>
                      <div className="flex items-center ml-4">
                        <HealthBadge value={focusArea.health} level={focusArea.healthLevel} lang={lang}/>
                      </div>
                    </div>
                    <div className="mt-3">
                      <TwoBars value1={focusArea.scoreSelf} value2={focusArea.scoreOther}
                               max={surveyResult.nrAnswerOptions}
                               results1={focusArea.scoreSelf.toFixed(1) + "/" + surveyResult.nrAnswerOptions}
                               results2={focusArea.scoreOther.toFixed(1) + "/" + surveyResult.nrAnswerOptions}/>
                    </div>
                    {
                      aiFocusArea &&
                        <div className="mt-3 text-sm text-gray-400">{aiFocusArea.summary}</div>
                    }
                  </div>
                )
              })
          }
        </div>
{/*Improvement potential- Areas with improvement potential 
NOTE: Need to add show more logic 
*/}
        {
          surveyResult.focusAreas.filter(f => f.healthLevel < 2).length > 3 &&
          <CustomButton 
              buttonType="transparentBase" 
              className="mt-4 mx-auto cursor-pointer" 
              size="sm" 
              onClick={() => handleShowMore(isShowMoreBad, ArrayName.BAD)}
              leftIcon={<img src={isShowMoreBad ? iconMinus : iconPlus2} alt="info" />}
              style={{ width: "unset" }} 
              > 
              {isShowMoreBad ? t("btn-show-less") : t("btn-show-more")}
          </CustomButton> 
        }

        <hr className="border-indigo-700 my-4"/>

        {
          blindSpots > 0 &&
            <>
              <div className="flex justify-between items-center cursor-pointer"
                 onClick={() => {
                  setShowBlindSpot(!showBlindSpot);
                  setVisibleCount(SHOW_MORE_INCREMENT);
                  }}>
                <p>{t("blind-spots")} ({blindSpots})</p>
                <img src={iconDown} alt="open"
                     className={"h-4 w-4 hover:bg-indigo-700 rounded-[12px]" + (showBlindSpot ? " rotate-180" : "")}/>
              </div>

              {showBlindSpot && renderQuestions('blind_spots')}

              <hr className="border-indigo-700 my-4"/>
            </>
        }

        {
          growthPriorities > 0 &&
            <>
              <div className="flex justify-between items-center cursor-pointer"
                 onClick={() => {
                  setShowGrowth(!showGrowth);
                  setVisibleCount(SHOW_MORE_INCREMENT);
                  }}>
                <p>{t("growth-priorities")} ({growthPriorities})</p>
                <img src={iconDown} alt="open"
                     className={"h-4 w-4 hover:bg-indigo-700 rounded-[12px]" + (showGrowth ? " rotate-180" : "")}/>
              </div>

              {showGrowth && renderQuestions('growth_priorities')}

              <hr className="border-indigo-700 mt-4"/>
            </>
        }

      </div>
    )
  }

  const createFeedbackRequest = (surveyId: string) => {
    api.post("client/survey_request/create", {survey_id: surveyId}).then((response) => {
      if (response.json.status === "ok") {
        navigate("/feedback/survey/" + response.json.surveyRequestId);
      } else {
        alert("Error");
      }
    })
  }

  return (
    <>

      <div className="flex justify-between items-center mt-6">
        <div className="flex items-center">
          <div className="z-heading1">
            {showLanguageString(lang, surveyResult.categoryNameSv, surveyResult.categoryName)} {t("insights-report")}
          </div>
          <div className="ml-2 p-1 flex item-center justify-center cursor-pointer  rounded-[20px] hover:bg-indigo-700" 
            onClick={() => {
              setVisibleCount(SHOW_MORE_INCREMENT)
              setShowInfoModal(!showInfoModal)}}>
            <img src={iconInfo} alt="info" className="h-6 w-6 coursor-pointer" />
          </div>
         {
          otherRequests.length > 1 &&
            <div>
              <select className="ml-4 z-select small" onChange={(e) => navigate("/dev-plan/survey/result/" + e.target.value + "/summary")}>
                {
                  otherRequests.map(or => (
                    <option key={"o_" + or.id} value={or.id}>{or.date}</option>
                    
                  ))
                }
              </select>
            </div>
         }
        </div>


          <CustomButton
              buttonType="transparentBase"
              size="sm"
              disabled={!canCreateRequest}
              onClick={() => createFeedbackRequest(surveyId)}
              leftIcon={<img src={iconPlus2} alt="info" className="h-4 w-4"/>}
              style={{ width: "unset" }}
          >
            {lang === "sv" ? "Ny mätning" : "New assessment"}
          </CustomButton>

      </div>

      <div className="mt-4 bg-indigo-900 p-4 rounded-md border-indigo-800 border">
        <div className="flex items-center justify-between">
          <div className="z-title1">
          {showLanguageString(lang, surveyResult.categoryNameSv, surveyResult.categoryName)}&nbsp;
            {lang === "sv" ? "översikt" : "snapshot"}
          </div>
          <div className="z-badge large transparent cursor-pointer hover:bg-indigo-700 hover:text-white hover:border-indigo-700"
               onClick={() => setFocusAreasSorted("health_high")}>{t("see-full-result")}
          </div>
        </div>
        <div className="mt-2 text-gray-400">
          {lang === "sv" ? "Resultatet baseras på " : "Summarized results based on "}
          {surveyResult.focusAreas.length} {lang === "sv" ? "områden (maximalt betyg per område" :
          "Areas (maximum score per area"}: {surveyResult.nrAnswerOptions})
        </div>

        <div className="flex flex-wrap">
          <div className="main-half-col mt-8 text-center">
            <div className="mx-auto" style={{width: 306}}>
              <HalfCircleChart
                value1={surveyResult.scoreSelf}
                value2={surveyResult.scoreOther}
                maxValue={surveyResult.nrAnswerOptions * surveyResult.focusAreas.length}
                circleNumbers={true}
                type="focus_area"
              />
              <div className="mt-6 flex items-center justify-center">
                <div className="h-4 w-4 rounded-full bg-peach-500"></div>
                <div className="text-sm ml-2">{t("self-assessment")}</div>
                <div className="h-4 w-4 rounded-full bg-peach-300 ml-4"></div>
                <div className="text-sm ml-2">Feedback</div>
              </div>
            </div>
          </div>

          <div className="main-half-col mt-8">
            <div className="bg-indigo-800 p-4 rounded-[4px] border-indigo-700 border-1">
              {
                feedbackType === FeedbackType.FEEDBACK &&
                  <div className="mb-2">
                    <SurveyBadge lang={lang} value={surveyResult.scoreSelf / (surveyResult.nrAnswerOptions * surveyResult.focusAreas.length)}
                                 type="score" />
                  </div>
              }

              <div className="flex justify-between items-center">
                <div>{t("self-assessment")}</div>
                <div className="flex items-center">
                  <span className="z-heading1">{surveyResult.scoreSelf.toFixed(1)}</span>
                  <span className="font-medium">/{(surveyResult.nrAnswerOptions * surveyResult.focusAreas.length).toFixed(0)}</span>
                </div>
              </div>
            </div>

            <div className="bg-indigo-800 mt-2 bg-indigo-700 p-4 rounded-[4px] border-indigo-700 border-1">
              {
                feedbackType === FeedbackType.FEEDBACK &&
                  <div className="mb-2">
                      <SurveyBadge lang={lang} value={surveyResult.scoreOther / (surveyResult.nrAnswerOptions * surveyResult.focusAreas.length)}
                                   type="score" />
                  </div>
              }

              <div className="flex justify-between items-center">
                <div>Feedback</div>
                <div className="flex items-center">
                  <span className="z-heading1">
                    {feedbackType === FeedbackType.ONLY_SELF ? "-" : surveyResult.scoreOther.toFixed(1)}
                  </span>
                  <span className="font-medium">/{(surveyResult.nrAnswerOptions * surveyResult.focusAreas.length).toFixed(0)}</span>
                </div>
              </div>
            </div>

            <div className="bg-indigo-800 mt-2 bg-indigo-700 p-4 rounded-[4px] border-indigo-700 border-1">
              {
                feedbackType === FeedbackType.FEEDBACK &&
                  <div className="mb-2">
                      <SurveyBadge lang={lang}
                                   value={Math.abs(surveyResult.scoreSelf - surveyResult.scoreOther) / surveyResult.focusAreas.length}
                                   type="gap" />
                  </div>
              }

              <div className="flex justify-between items-center">
                <div>{t("perception-gap")}</div>
                <div className="z-heading1">
                  {feedbackType === FeedbackType.ONLY_SELF ? "-" : Math.abs(surveyResult.scoreSelf - surveyResult.scoreOther).toFixed(1)}
                </div>
              </div>
            </div>

            <div className="bg-indigo-800 p-4 rounded-[4px] mt-2 bg-indigo-700 p-4 rounded-[4px] border-indigo-700 border-1">
              {
                feedbackType === FeedbackType.FEEDBACK &&
                  <div className="mb-2">
                      <SurveyBadge lang={lang}
                                   value={surveyResult.spreadRespondentsNormalized}
                                   type="feedback_alignment" />
                  </div>
              }


              <div className="flex justify-between items-center">
                <div>{t("feedback-alignment")}</div>
                <div className="z-heading1">
                  {feedbackType === FeedbackType.ONLY_SELF ? "-" : normalizeFromZeroToOne(surveyResult.spreadRespondentsNormalized, 1, surveyResult.nrAnswerOptions).toFixed(1)}/{surveyResult.nrAnswerOptions}
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>


      {
        feedbackType === FeedbackType.FEEDBACK &&
          <div className="mt-4 bg-indigo-900 p-4 rounded-[6px] border-indigo-800 border-1">
            <p className="z-title1">{t("summary")}</p>
            <div className="flex flex-wrap mt-2 gap-y-2">
              <div className="main-half-col pr-4">
                {splitLongText(aiInsights.summary, 0)}
              </div>
              <div className="main-half-col">
                {splitLongText(aiInsights.summary, 1)}
              </div>
            </div>
          </div>
      }

      <div className="mt-4 bg-indigo-900 p-4 rounded-[6px] border-indigo-800 border-1">
        <div className="flex justify-between items-center">
          <div className="z-title1">{t("strengths")}</div>
          {
            hasTopHealth > 0 &&
              <div className="z-badge large transparent cursor-pointer  hover:bg-indigo-700 hover:text-white hover:border-indigo-700"
                   onClick={() => setFocusAreasSorted("health_high")}>{t("see-full-result")}
              </div>
          }
        </div>

        {showTopHealthFocusAreas(hasTopHealth)}
      </div>

      <div className="mt-4 bg-indigo-900 p-4 rounded-[6px] border-indigo-800 border-1">
        <div className="flex justify-between items-center">
          <div className="z-title1">{lang === "sv" ? "Utvecklingspotential" : "Improvement potential"}</div>
          {
            hasImprovementHealth > 0 &&
              <div className="z-badge large transparent cursor-pointer  hover:bg-indigo-700 hover:text-white hover:border-indigo-700"
                   onClick={() => setFocusAreasSorted("health_high")}>{t("see-full-result")}
              </div>
          }
        </div>


        {showTopImprovementFocusAreas(hasImprovementHealth)}
      </div>

      {
        showInfoModal &&
          <ResultInfoModal close={() => setShowInfoModal(false)}/>
      }
    </>
  );
}

export default SurveyResultSummary;